//@flow
import System from './System';
import t from '../i18n/I18n';
import Environment from './Environment';
import Theme from './Theme';
import ConnectionUtil from '../util/session/ConnectionUtil';
import GetCustomPropsQuery from '../controllers/GetCustomPropsQuery';
import HashMap from '../protocol/HashMap';
import { OsUtil } from './OsUtil';

const debug = require('../debug/debug')('wc:system:Bootstrap');

export default class Bootstrap {
  _system: System;
  appName: string;

  constructor() {
    if (debug.enabled) debug('Starting application: %s with environment: %s', window.ARGS.appName);
    if (debug.enabled) debug('All ENV: %o', process.env);
    this.appName = window.appName ? window.appName : 'WEB_CLIENT';
    this._system = System.getInstance();
    this.setupAppName();
    this.setupEnvironment();
    this.setupTheme();
    Bootstrap.setupBaseURL();
    this.setupNetwork();
    this.setUpCustomisedProps();
  }

  setupAppName(): void {
    this._system.put(System.APP_CODE_KEY, this.appName);
    const appCode: string = this._system.get(System.APP_CODE_KEY, '').toUpperCase();
    let appNameKey = '';
    switch (appCode) {
      case 'WEB':
        appNameKey = System.APP_NAME.WEB;
        break;
      case 'VAB':
        appNameKey = System.APP_NAME.VAB;
        break;
      case 'WEB_CLIENT':
        appNameKey = System.APP_NAME.WEBCLIENT;
        break;
    }
    if (!appNameKey) {
      if (debug.enabled) {
        debug('App code is not passed to the application or wrong value is passed. Aborting the app launch.');
      }
      throw new Error('App code is not passed to the application or wrong value is passed. Aborting the app launch.');
    }
    if (debug.enabled) {
      debug('Setting app name in the System store. App name key: %s. App Name: %s', appNameKey, t(appNameKey));
    }

    this._system.put(System.APP_NAME_KEY, t(appNameKey));
  }

  setupEnvironment(): void {
    const appName = System.getAppName();
    const environment: ?Environment = appName === System.APP_NAME.VAB ? Environment.DESKTOP : Environment.BROWSER;
    if (!environment) {
      if (debug.enabled) debug('Not able to detect any environment. Please make sure environment is passed.');
      throw new Error('Not able to detect any environment. Please make sure environment is passed.');
    }
    this._system.put(System.ENVIRONMENT_KEY, environment);
  }

  setupTheme(): void {
    if (!System.getTheme()) {
      const environment: Environment = System.getEnvironment();
      let theme: Theme = Theme.getTheme();
      if (!theme) {
        if (environment.isBrowser()) {
          theme = Theme.LIGHT;
        } else if (environment.isDesktop()) {
          theme = Theme.DARK;
        }
      }
      if (debug.enabled) debug('Theme setup. Theme: %o', theme);
      this._system.put(System.THEME_KEY, theme);
    }
  }

  static setupBaseURL(): void {
    if (System.isBrowserApp()) {
      window.baseApiUrl = '';
    } else {
      window.baseApiUrl = 'http://127.0.0.1:7770';
    }
  }

  setupNetwork(): void {
    if (debug.enabled) debug('Secure and unsecure call setup');

    let UNSECURE_REMOTE_CALL_URL: string;
    if (System.isBrowserApp()) {
      UNSECURE_REMOTE_CALL_URL = ConnectionUtil.getBaseUrl() + '/webclient/unsecure';
    } else {
      UNSECURE_REMOTE_CALL_URL =
        ConnectionUtil.getBaseUrl() + ConnectionUtil.DESKTOP_CONTEXT + ConnectionUtil.UNSECURE_REMOTE_CALL_SUFFIX;
    }
    this._system.put(ConnectionUtil.UNSECURE_REMOTE_CALL_URL, UNSECURE_REMOTE_CALL_URL);

    let SECURE_REMOTE_CALL_URL: string;
    if (System.isBrowserApp()) {
      SECURE_REMOTE_CALL_URL = ConnectionUtil.getBaseUrl() + '/webclient/vabremoting/';
    } else {
      SECURE_REMOTE_CALL_URL =
        ConnectionUtil.getBaseUrl() + ConnectionUtil.DESKTOP_CONTEXT + ConnectionUtil.SECURE_REMOTE_CALL_SUFFIX;
    }

    this._system.put(ConnectionUtil.SECURE_REMOTE_CALL_URL, SECURE_REMOTE_CALL_URL);
  }

  setUpCustomisedProps() {
    if (System.isDesktop()) {
      new GetCustomPropsQuery().run().then(
        (resp) => {
          //FAV_ICON //HOME_HEADER //LOGO  //LOGIN_BG
          const items: HashMap = resp.getResponseMap();
          try {
            if (items.size) {
              window.homeHeaderExists = items.get('HOME_HEADER');
              if (OsUtil.isWindows() && window.homeHeaderExists)
                window.homeHeaderExists = new URL(window.homeHeaderExists);
              window.logoExists = items.get('LOGO');
              if (OsUtil.isWindows() && window.logoExists)
                window.logoExists = new URL(window.logoExists);
              window.loginBgExists = items.get('LOGIN_BG');
              if (OsUtil.isWindows() && window.loginBgExists)
                window.loginBgExists = new URL(window.loginBgExists);
              if (items.containsKey('LOGIN_OVERLAY')) {
                window.loginOverlay = JSON.parse(items.get('LOGIN_OVERLAY'));
                if (!window.loginOverlay || window.loginOverlay.length == 0)
                  window.loginOverlay = null;
              }
              if (items.containsKey('LOGIN_BG_SIZES')) {
                window.loginBgSrcSetSizes = JSON.parse(items.get('LOGIN_BG_SIZES'));
                if (!window.loginBgSrcSetSizes || window.loginBgSrcSetSizes.length == 0) {
                  window.loginBgSrcSetSizes = null;
                } else if (OsUtil.isWindows()) {
                  window.loginBgSrcSetSizes = window.loginBgSrcSetSizes
                    .map((o) => { return {size: o.size, path: new URL(o.path)}; });
                }
              }
              if (items.containsKey('HOME_HEADER_SIZES')) {
                window.homeHeaderSrcSetSizes = JSON.parse(items.get('HOME_HEADER_SIZES'));
                if (!window.homeHeaderSrcSetSizes || window.homeHeaderSrcSetSizes.length == 0) {
                  window.homeHeaderSrcSetSizes = null;
                } else if (OsUtil.isWindows()) {
                  window.homeHeaderSrcSetSizes = window.homeHeaderSrcSetSizes
                    .map((o) => { return {size: o.size, path: new URL(o.path)}; });
                }
              }

              window.gs.updateNotLoggedInBlock ? window.gs.updateNotLoggedInBlock() : null;
            }
          } catch (ignored) {
            console.log(ignored);
          }

          if (debug.enabled) {
            debug('getcustom query response');
            debug(resp);
          }
        },
        (error) => {
          if (debug.enabled) {
            debug('getcustom query error');
            debug(error);
          }
        }
      );
    }
  }
}
